import { useEffect, useState, useCallback } from "react";
import { Clapperboard, Loader2, Sparkles, Image as ImageIcon, Film, Copy, Check, Upload, Trash2, ExternalLink } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const TABS = [
  { id: "scripts", label: "Scripts TikTok", icon: Sparkles },
  { id: "affiches", label: "Affiches (IA)", icon: ImageIcon },
  { id: "media", label: "Médiathèque", icon: Film },
];

function CopyBtn({ text, id }) {
  const [done, setDone] = useState(false);
  return (
    <button data-testid={`copy-${id}`} onClick={() => { navigator.clipboard?.writeText(text || ""); setDone(true); toast.success("Copié"); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover">
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copier
    </button>
  );
}

export default function Studio() {
  const { user } = useAuth();
  const canDelete = user?.role === "patron" || user?.role === "associe";
  const [tab, setTab] = useState("scripts");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");

  // scripts
  const [tone, setTone] = useState("dynamique");
  const [genScript, setGenScript] = useState(false);
  const [script, setScript] = useState(null);
  // affiches
  const [prompt, setPrompt] = useState("");
  const [useImg, setUseImg] = useState(false);
  const [genPoster, setGenPoster] = useState(false);
  // media
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);
  const loadMedia = useCallback(() => { api.get("/admin/studio/media").then(({ data }) => setMedia(data)).catch(() => setMedia([])); }, []);
  useEffect(() => { loadMedia(); }, [loadMedia]);

  const fileUrl = (u) => `${process.env.REACT_APP_BACKEND_URL}${u}`;

  const runScript = async () => {
    setGenScript(true); setScript(null);
    try {
      const { data } = await api.post("/admin/studio/script", { product_id: productId, tone });
      setScript(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Échec de la génération"); }
    finally { setGenScript(false); }
  };

  const runPoster = async () => {
    if (prompt.trim().length < 3) return toast.error("Décrivez l'affiche souhaitée");
    setGenPoster(true);
    try {
      await api.post("/admin/studio/poster", { product_id: productId, prompt: prompt.trim(), use_product_image: useImg });
      toast.success("Affiche générée ✨");
      setPrompt("");
      loadMedia();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Échec de la génération"); }
    finally { setGenPoster(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const q = `?product_id=${encodeURIComponent(productId)}&title=${encodeURIComponent(file.name)}`;
      await api.post(`/admin/studio/media/upload${q}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ajouté à la médiathèque");
      loadMedia();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try { await api.delete(`/admin/studio/media/${id}`); toast.success("Supprimé"); loadMedia(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const shareLink = (u) => { navigator.clipboard?.writeText(fileUrl(u)); toast.success("Lien copié — collez-le sur TikTok / WhatsApp"); };

  const inputCls = "border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";
  const posters = media.filter((m) => m.kind === "image");

  return (
    <div className="space-y-6" data-testid="studio-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink flex items-center gap-2">
          <Clapperboard className="w-7 h-7 text-brand" /> Studio Pub
        </h1>
        <p className="text-brand-muted mt-1">Créez vos scripts, affiches et vidéos pour TikTok.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={productId} onChange={(e) => setProductId(e.target.value)} className={inputCls} data-testid="studio-product">
          <option value="">Aucun produit (général)</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="flex gap-1 bg-brand-cream rounded-full p-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`studio-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${tab === t.id ? "bg-brand text-white" : "text-brand-sub hover:bg-white"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* SCRIPTS */}
      {tab === "scripts" && (
        <div className="space-y-4" data-testid="studio-scripts">
          <div className="bg-white border border-brand-line rounded-2xl p-5 flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-brand-muted">Ton</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className={`${inputCls} block mt-1`} data-testid="studio-tone">
                <option value="dynamique">Dynamique / vendeur</option>
                <option value="chaleureux">Chaleureux / familial</option>
                <option value="humoristique">Humoristique</option>
                <option value="éducatif">Éducatif / expert</option>
                <option value="urgent">Urgent / promo</option>
              </select>
            </div>
            <button onClick={runScript} disabled={genScript} data-testid="generate-script-btn"
              className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
              {genScript ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />} Générer le script
            </button>
          </div>

          {script && (
            <div className="grid gap-3" data-testid="script-result">
              <div className="bg-white border border-brand-line rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2"><h3 className="font-heading font-bold text-brand-ink">🎬 Script vidéo</h3><CopyBtn text={script.script} id="script" /></div>
                <p className="text-sm text-brand-sub whitespace-pre-line">{script.script}</p>
              </div>
              <div className="bg-white border border-brand-line rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2"><h3 className="font-heading font-bold text-brand-ink">📝 Légende</h3><CopyBtn text={script.caption} id="caption" /></div>
                <p className="text-sm text-brand-sub whitespace-pre-line">{script.caption}</p>
              </div>
              <div className="bg-white border border-brand-line rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2"><h3 className="font-heading font-bold text-brand-ink"># Hashtags</h3><CopyBtn text={script.hashtags} id="hashtags" /></div>
                <p className="text-sm text-brand" data-testid="script-hashtags">{script.hashtags}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AFFICHES */}
      {tab === "affiches" && (
        <div className="space-y-4" data-testid="studio-affiches">
          <div className="bg-white border border-brand-line rounded-2xl p-5 space-y-3">
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Décrivez l'affiche : ex. « Promo -10% sur le miel, ambiance premium violet & or, texte NOUMABOU »"
              className={`${inputCls} w-full`} data-testid="poster-prompt" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-brand-sub cursor-pointer">
                <input type="checkbox" checked={useImg} onChange={(e) => setUseImg(e.target.checked)} className="w-4 h-4 accent-brand" data-testid="poster-use-image" />
                Utiliser la photo du produit sélectionné
              </label>
              <button onClick={runPoster} disabled={genPoster} data-testid="generate-poster-btn"
                className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
                {genPoster ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />} Générer l'affiche
              </button>
            </div>
            {genPoster && <p className="text-xs text-brand-muted">Génération en cours (10-30 sec)…</p>}
          </div>

          {posters.length === 0 ? <p className="text-sm text-brand-muted">Aucune affiche générée pour l'instant.</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="posters-grid">
              {posters.map((m) => (
                <div key={m.id} className="bg-white border border-brand-line rounded-xl overflow-hidden group">
                  <img src={fileUrl(m.url)} alt={m.title} className="w-full aspect-[3/4] object-cover" />
                  <div className="p-2 flex items-center justify-between">
                    <button onClick={() => shareLink(m.url)} className="text-xs font-semibold text-brand inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Lien</button>
                    <a href={fileUrl(m.url)} target="_blank" rel="noreferrer" className="text-xs text-brand-sub inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Ouvrir</a>
                    {canDelete && <button onClick={() => del(m.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MEDIATHEQUE */}
      {tab === "media" && (
        <div className="space-y-4" data-testid="studio-media">
          <div className="bg-white border border-brand-line rounded-2xl p-5">
            <label data-testid="upload-media" className="inline-flex items-center gap-2 cursor-pointer bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              {uploading ? "Envoi…" : "Ajouter une vidéo / image"}
              <input type="file" accept="video/*,image/*" className="hidden" onChange={upload} disabled={uploading} />
            </label>
            <p className="text-xs text-brand-muted mt-2">Vidéo max 45 Mo (mp4/webm/mov). Filmez avec le téléphone, l'app garde et partage le lien pour TikTok/WhatsApp.</p>
          </div>

          {media.length === 0 ? <p className="text-sm text-brand-muted">Médiathèque vide.</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="media-grid">
              {media.map((m) => (
                <div key={m.id} data-testid={`media-${m.id}`} className="bg-white border border-brand-line rounded-xl overflow-hidden">
                  {m.kind === "video"
                    ? <video src={fileUrl(m.url)} controls className="w-full aspect-[3/4] object-cover bg-black" />
                    : <img src={fileUrl(m.url)} alt={m.title} className="w-full aspect-[3/4] object-cover" />}
                  <div className="p-2">
                    <p className="text-xs text-brand-sub truncate" title={m.title}>{m.title || "sans titre"}</p>
                    <p className="text-[10px] text-brand-muted">{m.source === "ia" ? "IA" : "Upload"} · {formatDate(m.created_at)}</p>
                    <div className="flex items-center justify-between mt-1">
                      <button onClick={() => shareLink(m.url)} data-testid={`share-${m.id}`} className="text-xs font-semibold text-brand inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Lien</button>
                      {canDelete && <button onClick={() => del(m.id)} data-testid={`delete-media-${m.id}`} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
